import { useState } from "react";
import { Lock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import SecurityRecoveryQuestionFields from "@/components/SecurityRecoveryQuestionFields";

/**
 * Seguridad de cuenta — no muestra respuestas en claro por defecto.
 */
export default function SecurityRecoveryAccountCard({
  configured = false,
  questionRows = [],
  onChangeRows,
  onSave,
  onClear,
  busy = false,
  disabled = false,
}) {
  const [editing, setEditing] = useState(false);
  const [editRows, setEditRows] = useState([]);
  const configuredCount = questionRows.filter((r) => String(r.q || "").trim()).length;

  const startEditing = () => {
    setEditRows(questionRows.map((r) => ({ q: r.q || "", answer: "" })));
    setEditing(true);
  };

  const handleSave = async () => {
    onChangeRows?.(editRows);
    await onSave?.();
    setEditing(false);
  };

  return (
    <div className="space-y-3" data-testid="security-recovery-account-card">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-slate-900 m-0 flex items-center gap-2">
            <Lock className="w-4 h-4 text-slate-600" />
            Seguridad de la cuenta
          </p>
          <p className="text-[11px] text-slate-600 mt-1 mb-0">
            Preguntas de recuperación configuradas:{" "}
            <strong>{configured ? configuredCount || 3 : 0}</strong>
            {configured ? (
              <span className="text-emerald-800"> — activas</span>
            ) : (
              <span className="text-amber-800"> — pendientes de configurar</span>
            )}
          </p>
        </div>
        {!editing && !disabled ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={startEditing}
            data-testid="security-recovery-edit-btn"
          >
            {configured ? "Actualizar preguntas" : "Configurar preguntas"}
          </Button>
        ) : null}
      </div>

      {editing ? (
        <div className="rounded-md border border-slate-200 bg-slate-50/80 p-3 space-y-3">
          <p className="text-[10px] text-slate-600 m-0 leading-snug">
            Por seguridad no mostramos sus respuestas anteriores. Elija las preguntas y escriba las{" "}
            <strong>nuevas</strong> respuestas que usará en «Olvidé mi contraseña».
          </p>
          <SecurityRecoveryQuestionFields
            rows={editRows}
            onChange={setEditRows}
            disabled={busy}
            testIdPrefix="buyer-security"
          />
          <div className="flex flex-wrap gap-2 justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 text-xs"
              onClick={() => setEditing(false)}
              disabled={busy}
            >
              Cancelar
            </Button>
            {configured ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={onClear}
                disabled={busy}
              >
                Quitar personalización
              </Button>
            ) : null}
            <Button
              type="button"
              size="sm"
              className="h-8 text-xs bg-[#0F2D5C] hover:bg-[#0a1f42]"
              onClick={handleSave}
              disabled={busy}
              data-testid="security-recovery-save-btn"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
              Guardar preguntas
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-[10px] text-slate-500 m-0">
          Use «Olvidé mi contraseña» en la pantalla de acceso para recuperar su cuenta con una de estas preguntas.
        </p>
      )}
    </div>
  );
}
